import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSelector, useDispatch } from 'react-redux';
import { getChallenges } from '../redux/data/dataActions.js';
import { setChallengesLoading, setSelectedChallenge, setTabSelected } from "../redux/data/dataSlice.js";
import { setupChallengeMetaData, setupBaseMetaData } from "../utils/metaData.js";
import { injectPageScripts } from "../utils/loadScripts";
import ChallengeCard from "./challenges/challengeCard.jsx";
import CompletedChallengeCard from "./challenges/completedChallengeCard.jsx";
import ChallengeModal from "./challenges/challengeModal.jsx";
import TabSectionHeader from "./tabSectionHeader.jsx";
import "../assets/css/header.css";
import "../assets/css/challenges.css";
import CompletedChallengeModal from "./challenges/completedChallengeModal.jsx";

function Challenges() {
    const dispatch = useDispatch();
    const params = useParams();
    const challengeData = useSelector(state => state.data.challengeData);
    const selectedChallenge = useSelector(state => state.data.selectedChallenge);
    const userType = useSelector(state => state.data.userType);
    const [activeChallengeModalOpen, setActiveChallengeModalOpen] = useState(false);
    const [completedChallengeModalOpen, setCompletedChallengeModalOpen] = useState(false);
    const [selectedChallengeTab, setSelectedChallengeTab] = useState("active");
    const evergreenSubheader = "Earn exclusive rewards by joining NBA ID Challenges. Complete activities in the NBA App or on NBA.com\
        throughout the season, so check back often so you don’t miss out!";

    const addPageAnalytics = () => {
        window.digitalData.page.pageInfo.pageName = "nba:membership-hub:challenges";
        window.digitalData.page.pageInfo.siteSection = "nba:membership-hub";
        window.digitalData.page.content = { categoryTitle: "Challenges", categoryType: "membership-hub" };
        window.digitalData.site = { propertyName: "nba:web", league: "nba" };
        if (window.digitalData.user) {
            window.digitalData.user.userState = "authenticated";
            window.digitalData.user.userType = userType;
        }

        console.log("Challenges: analytics added");
    };

    const closeModal = () => {
        dispatch(setSelectedChallenge(null));
        setActiveChallengeModalOpen(false);
        setCompletedChallengeModalOpen(false);
        setupBaseMetaData();
    };

    const openActiveChallengeModal = challenge => {
        dispatch(setSelectedChallenge(challenge));
        setupChallengeMetaData(challenge);
        setActiveChallengeModalOpen(true);
    };

    const openCompletedChallengeModal = challenge => {
        dispatch(setSelectedChallenge(challenge));
        setupChallengeMetaData(challenge);
        setCompletedChallengeModalOpen(true);
    };

    const renderChallenges = () => {
        if (selectedChallengeTab === "active") {
            if (challengeData && challengeData.active && challengeData.active.length > 0) {
                return challengeData.active.map((challenge, i) =>
                    <ChallengeCard challenge={challenge} key={i} />);
            } else {
                return <div className="no-challenges">
                    There are no active challenges at the moment. Stay tuned - new opportunities to earn rewards are on the way!
                </div>
            }
        } else {
            if (challengeData && challengeData.completed && challengeData.completed.length > 0) {
                return <div className="completed-challenges-container">
                    {challengeData.completed.map((challenge, i) => <CompletedChallengeCard challenge={challenge} key={i} />)}
                </div>
            } else {
                return <div className="no-challenges">
                    Your challenge history lives here. Don’t see your first challenge yet? Don’t worry, it’ll show up once the challenge is over. 
                </div>
            }
        }
    }

    useEffect(() => {
        console.log("Challenges: useEffect[selectedChallengeModalActive]");

        const bodyElement = document.getElementById("body");
        if (bodyElement) {
            if (activeChallengeModalOpen || completedChallengeModalOpen) {
                if (!bodyElement.className.includes("modal-noscroll")) {
                    bodyElement.className += " modal-noscroll";
                }
            } else {
                if (bodyElement.className.includes("modal-noscroll")) {
                    bodyElement.className = bodyElement.className.replace(" modal-noscroll", "");
                }
            }
        }
    }, [activeChallengeModalOpen, completedChallengeModalOpen]);

    useEffect(() => {
        console.log("Challenges: useEffect[findbyChallengeId]");

        const findChallengeById = id => {
            if (id && challengeData) {
                const active = challengeData.active.find(element => element.id === parseInt(id));
                if (active) return { status: "active", challenge: active };
                else {
                    const comp = challengeData.completed.find(element => element.activity_id === id);
                    if (comp) return { status: "completed", challenge: comp };
                }
            }
            return null;
        };

        if (params && params.challengeId) {
            const found = findChallengeById(params.challengeId);
            if (found) {
                if (found.status === "active") openActiveChallengeModal(found.challenge);
                else if (found.status === "completed") openCompletedChallengeModal(found.challenge);
            } else {
                closeModal();
            }
        } else {
            closeModal();
            addPageAnalytics();
            setupBaseMetaData();
            injectPageScripts();
        }
    }, [params, challengeData, dispatch]);

    useEffect(() => {
        console.log("Challenges: useEffect[dispatch]");
        dispatch(setTabSelected("challenges"));
        console.log("tabSelected: challenges");    
    }, [dispatch]);

    useEffect(() => {
        if (!challengeData || challengeData === "[]" || challengeData.length === 0) {
            dispatch(setChallengesLoading(true));
            dispatch(getChallenges("challenges"));
        }
    }, [dispatch, challengeData]);

    return (<>
        <div className="challenges">
            <TabSectionHeader title={"NBA ID Challenges"} subtitle={evergreenSubheader} />
            <div className="title-bar"></div>
            <div className="challenges-container">
                <div className="challenge-tabs">
                    <div className={selectedChallengeTab === "completed" ? "tab selected" : "tab"}>
                        <button onClick={() => setSelectedChallengeTab("completed")}
                            data-track="click"
                            data-id="nba:membership-hub:challenges:tab"
                            data-type="tab"
                            data-text="Completed"
                        >Completed</button>
                    </div>
                    <div className={selectedChallengeTab === "active" ? "tab selected" : "tab"}>
                        <button onClick={() => setSelectedChallengeTab("active")}
                            data-track="click"
                            data-id="nba:membership-hub:challenges:tab"
                            data-type="tab"
                            data-text="Active"
                        >Active</button>
                    </div>
                </div>
                { renderChallenges() }
            </div>
        </div>
        {activeChallengeModalOpen && selectedChallenge && <ChallengeModal />}
        {completedChallengeModalOpen && selectedChallenge && <CompletedChallengeModal />}
    </>);
}

export default Challenges;
